/**
 * FinanceiroVehiclesBlock - Veículos vinculados a uma cobrança/assinatura
 *
 * Dois modos:
 *  - readOnly (padrão): apenas chips com placa · marca modelo (usado nos
 *    modais de DETALHES da cobrança e da assinatura).
 *  - editável: reaproveita o `VehiclePickerSection` e grava via a RPC
 *    `set_financeiro_vehicles` (usado nos modais de EDIÇÃO).
 *
 * Regras:
 *  - Só renderiza com o módulo de veículos habilitado no tenant.
 *  - Ciclos de assinatura herdam os veículos da assinatura mãe (herança
 *    resolvida no servidor por `list_financeiro_vehicles`); vínculos herdados
 *    aparecem com o rótulo "herdado" e não são editáveis no ciclo.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-18 v1.1.0 - Encaminha o contato principal ao seletor para
 *          listar também veículos associados indiretamente ao cliente.
 * RELEASE: 2026-08-18 v1.0.0 - Criação inicial (Financeiro x Veículos)
 */

import { useEffect, useState } from 'react';
import { Car, Save } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';
import {
  useFinanceiroVehicles,
  useSetFinanceiroVehicles,
  type FinanceiroVehicle,
} from '@/hooks/useFinanceiroVehicles';
import { VehiclePickerSection } from '@/components/financial/VehiclePickerSection';

interface FinanceiroVehiclesBlockProps {
  /** Cobrança alvo (informe cobrancaId OU assinaturaId) */
  cobrancaId?: string | null;
  /** Assinatura alvo */
  assinaturaId?: string | null;
  /** Cliente dono dos veículos — obrigatório no modo editável */
  customerId?: string | null;
  /** Contato principal do cliente para localizar veículos associados */
  contactId?: string | null;
  /** Permite alterar o vínculo (modais de edição) */
  editable?: boolean;
  /** Modal aberto (evita fetch desnecessário) */
  enabled?: boolean;
  className?: string;
}

/** Rótulo curto do veículo */
const label = (v: FinanceiroVehicle) =>
  [v.plate, [v.brand_name, v.model_name].filter(Boolean).join(' '), v.year]
    .filter(Boolean)
    .join(' · ');

export function FinanceiroVehiclesBlock({
  cobrancaId = null,
  assinaturaId = null,
  customerId = null,
  contactId = null,
  editable = false,
  enabled = true,
  className,
}: FinanceiroVehiclesBlockProps) {
  const vehiclesEnabled = useVehiclesModuleEnabled();
  const { data: linked, isLoading } = useFinanceiroVehicles(
    { cobrancaId, assinaturaId },
    vehiclesEnabled && enabled,
  );
  const setVehicles = useSetFinanceiroVehicles();

  // Seleção local do modo editável (sincronizada com o servidor)
  const [selection, setSelection] = useState<string[]>([]);
  useEffect(() => {
    setSelection((linked ?? []).map((v) => v.vehicle_id));
  }, [linked]);

  if (!vehiclesEnabled) return null;

  const items = linked ?? [];
  // Ciclo herdado: não editar pelo ciclo, e sim pela assinatura mãe
  const isInherited = items.some((v) => v.inherited);
  const canEdit = editable && !!customerId && !isInherited;

  // Modo leitura sem vínculos → não ocupa espaço
  if (!canEdit && !isLoading && items.length === 0) return null;

  const dirty =
    selection.length !== items.length ||
    selection.some((id) => !items.find((v) => v.vehicle_id === id));

  return (
    <div className={cn('space-y-2', className)}>
      {canEdit ? (
        <>
          <VehiclePickerSection
            customerId={customerId}
            contactId={contactId}
            value={selection}
            onChange={setSelection}
            enabled={enabled}
          />
          {dirty && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={setVehicles.isPending}
              onClick={() =>
                setVehicles.mutate({ cobrancaId, assinaturaId, vehicleIds: selection })
              }
            >
              <Save className="mr-1.5 h-3 w-3" />
              Salvar veículos
            </Button>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <Car className="h-3.5 w-3.5" />
            Veículos
            {isInherited && (
              <Badge variant="outline" className="h-4 text-[9px]">
                herdado da assinatura
              </Badge>
            )}
          </div>

          {isLoading ? (
            <Skeleton className="h-7 w-48" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {items.map((v) => (
                <Badge key={v.vehicle_id} variant="secondary" className="gap-1 text-[11px]">
                  <Car className="h-3 w-3" />
                  {label(v)}
                </Badge>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
